import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '@/lib/api/client';
import { auditKeys } from '@/features/audit/keys';
import { candidateKeys } from '@/features/candidates/keys';
import { duplicateKeys } from '@/features/duplicates/api';
import { eventKeys } from '@/features/events/api';
import { ruleKeys } from './keys';

export type Rule = Schemas['TrustRuleDto'];
export type RulePreview = Schemas['TrustRulePreviewDto'];
export type PreviewRecord = Schemas['TrustRulePreviewRecordDto'];
export type PreviewGroup = Schemas['TrustRulePreviewGroupDto'];
export type Suggestion = Schemas['TrustSuggestionDto'];
export type ActResult = Schemas['TrustRuleActResultDto'];
export type ActedGroup = Schemas['TrustRuleActedGroupDto'];
export type RefusedGroup = Schemas['TrustRuleRefusedGroupDto'];
export type RuleSourceReadiness = Schemas['RuleSourceReadinessDto'];
export type RecommendedRule = Schemas['RecommendedTrustRuleDto'];

export type RuleAction =
  'Publish' | 'Link' | 'ApplyChanges' | 'DismissUnlikely' | 'RejectOwnerDuplicate';
export type RuleMode = 'Off' | 'Suggest' | 'Act';

/** What a rule decides and where: a draft's or a saved rule's, as the preview takes it. */
export interface RuleDefinition {
  action: RuleAction;
  sourceId: string | null;
  minConfidence: number;
  allowedWarnings: string[];
}

export interface RuleInput {
  name: string;
  minConfidence: number;
  allowedWarnings: string[];
}

/** Every rule, oldest first, with the pending records each covers now. A rule's page reads it here too. */
export const rulesQuery = () =>
  queryOptions({
    queryKey: ruleKeys.list(),
    queryFn: () => unwrap(api.GET('/api/scraping/rules')),
  });

/** The all-sources rules an action is missing, by the API's recommended bars. Empty once none is. */
export const recommendedQuery = () =>
  queryOptions({
    queryKey: ruleKeys.recommended(),
    queryFn: () => unwrap(api.GET('/api/scraping/rules/recommended')),
  });

export const definitionOf = (rule: Rule): RuleDefinition => ({
  action: rule.action as RuleAction,
  sourceId: rule.sourceId,
  minConfidence: rule.minConfidence,
  allowedWarnings: rule.allowedWarnings,
});

/** What the definition would have done over the last `days` days. Reads only. */
export const rulePreviewQuery = (
  definition: RuleDefinition,
  ruleId: string | undefined,
  days: number,
) =>
  queryOptions({
    queryKey: ruleKeys.preview(definition, ruleId, days),
    queryFn: () =>
      unwrap(
        api.GET('/api/scraping/rules/preview', {
          params: {
            query: {
              action: definition.action,
              sourceId: definition.sourceId ?? undefined,
              minConfidence: definition.minConfidence,
              allowedWarning: definition.allowedWarnings,
              ruleId,
              days,
            },
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });

/** A rule's change moves what it suggests, so the candidates and the trail are read again. */
export function useRefreshRules() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ruleKeys.all }),
      queryClient.invalidateQueries({ queryKey: candidateKeys.all }),
      queryClient.invalidateQueries({ queryKey: auditKeys.all }),
    ]);
}

export function useCreateRule() {
  const refresh = useRefreshRules();
  return useMutation({
    mutationFn: (input: RuleInput & { action: RuleAction; sourceId: string | null }) =>
      unwrap(api.POST('/api/scraping/rules', { body: input })),
    onSuccess: refresh,
  });
}

export function useUpdateRule(id: string) {
  const refresh = useRefreshRules();
  return useMutation({
    mutationFn: (input: RuleInput) =>
      unwrap(api.PUT('/api/scraping/rules/{id}', { params: { path: { id } }, body: input })),
    onSettled: refresh,
  });
}

export function useSetRuleMode() {
  const refresh = useRefreshRules();
  return useMutation({
    mutationFn: ({ id, mode }: { id: string; mode: RuleMode }) =>
      unwrap(
        api.PUT('/api/scraping/rules/{id}/mode', { params: { path: { id } }, body: { mode } }),
      ),
    onSettled: refresh,
  });
}

/** Leaves the rule's page to the caller: it navigates away first, then reads the rules again. */
export function useDeleteRule(id: string) {
  return useMutation({
    mutationFn: () => unwrap(api.DELETE('/api/scraping/rules/{id}', { params: { path: { id } } })),
  });
}

/** Adds the missing ones in Suggest, all or none; answers the rules it added, none if someone was first. */
export function useAddRecommended() {
  const refresh = useRefreshRules();
  return useMutation({
    mutationFn: () => unwrap(api.POST('/api/scraping/rules/recommended')),
    onSuccess: refresh,
  });
}

/** A rule set to Act decides the pending records (or open groups) it covers now; each lands in the trail as the rule. */
export function useActNow() {
  const queryClient = useQueryClient();
  const refresh = useRefreshRules();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.POST('/api/scraping/rules/{id}/act', { params: { path: { id } } })),
    onSettled: () =>
      Promise.all([
        refresh(),
        queryClient.invalidateQueries({ queryKey: eventKeys.all }),
        queryClient.invalidateQueries({ queryKey: duplicateKeys.all }),
      ]),
  });
}
